'use client';

import { DotLottie, DotLottieReact } from '@lottiefiles/dotlottie-react';
import { ComponentProps, useEffect, useRef, useState } from 'react';

import { Picture } from '@flipster/react/design-utils/picture';
import { Video } from '@flipster/react/design-utils/video';
import { Styles, css } from '@flipster/styled-system/css';

type VideoComponentProps = ComponentProps<typeof Video>;

type Nullable<T> = { [K in keyof T]: T[K] | null };

interface ImgOrVideoProps {
  width?: number;
  height?: number;
  video?: Partial<Nullable<VideoComponentProps['src']>>;
  img?: VideoComponentProps['poster'];
  alt?: string | null;
  fetchPriority?: 'high' | 'low' | 'auto';
  loading?: 'lazy';
  css?: Styles;
}

export const ImgOrVideo = ({
  width,
  height,
  img,
  alt,
  video,
  fetchPriority,
  loading,
  css: cssProps,
}: ImgOrVideoProps) => {
  if (video && video.base && img)
    return <Video poster={img} src={video as VideoComponentProps['src']} width={width} css={cssProps} />;

  if (img)
    return (
      <Picture
        src={img}
        alt={alt || 'flipster image'}
        width={width}
        height={height}
        fetchPriority={fetchPriority}
        loading={loading}
        css={cssProps}
      />
    );

  return null;
};

export const contentfulImageUrl = (url: string) => {
  if (url.endsWith('.svg')) return url;
  if (url.endsWith('.lottie')) return url;
  return url + '?fm=webp&q=80';
};

const checkLottie = (url: string) => url.endsWith('.lottie');

interface ImgOrLottieProps {
  url?: string | null;
  alt?: string;
  css?: Styles;
}

export const ImgOrLottie = ({ url, alt, css: cssProps }: ImgOrLottieProps) => {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const lottieRef = useRef<DotLottie>(null);
  const isVisibleRef = useRef(true);
  const [isLoaded, setIsLoaded] = useState(false);

  const isLottie = url && checkLottie(url);

  useEffect(() => {
    if (!isLottie || !url) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    (async () => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const blob = await res.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      } catch {
        // load failed
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setBlobUrl(null);
      setIsLoaded(false);
    };
  }, [url, isLottie]);

  useEffect(() => {
    if (!isLottie || !canvasRef.current || !blobUrl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visible = entry.isIntersecting;
          isVisibleRef.current = visible;

          if (lottieRef.current && isLoaded) {
            if (visible) {
              lottieRef.current.play();
            } else {
              lottieRef.current.pause();
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '100px',
      },
    );
    observer.observe(canvasRef.current);

    return () => {
      observer.disconnect();
    };
  }, [isLottie, blobUrl, isLoaded]);

  if (!url) return null;

  if (isLottie) {
    if (!blobUrl) return null;

    return (
      <DotLottieReact
        dotLottieRefCallback={(dotLottie) => {
          lottieRef.current = dotLottie;

          if (dotLottie) {
            canvasRef.current = dotLottie.canvas as HTMLCanvasElement;
            dotLottie.addEventListener('load', () => {
              setIsLoaded(true);
              if (isVisibleRef.current) {
                dotLottie.play();
              }
            });
          }
        }}
        src={blobUrl}
        loop
        autoplay={false}
        className={css(cssProps)}
      />
    );
  }

  return <img src={contentfulImageUrl(url)} alt={alt} className={css(cssProps)} />;
};
